import React, { useEffect, useState, useMemo } from "react";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import {
  API_URL,
  GOOGLE_MAPS_MAP_ID,
  REACT_APP_GOOGLE_MAPS_KEY,
} from "../../actions/settings";
import { setKey, setLanguage, setRegion } from "react-geocode";
import { IonButton, IonIcon, IonRow, IonSelect, IonSelectOption } from "@ionic/react";
import GoogleMapClusters from "./GoogleMapClusters";
import { saveSharp } from "ionicons/icons";
import Popup from "./Popup";
// eslint-disable-next-line


interface Props {}

async function getBuildings(token: any,url:string) {
  return fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token,
    },
  }).then((data) => data.json());
}

async function getAllPrimaryFunctions(token: any) {
  return fetch(`${API_URL}/energystar/primaryfunctions`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token,
    },
  }).then((data) => data.json());
}

async function getAllSubTypes(token: any) {
  return fetch(`${API_URL}/subtypes`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token,
    },
  }).then((data) => data.json());
}

const getPrimaryFunctions = async (token: any, setPrimaryfunctions: any) => {
  const primaryfunctions = await getAllPrimaryFunctions(
    `Token ${token.slice(1, -1)}`
  );
  setPrimaryfunctions(primaryfunctions);
};
const getSubTypes = async (token: any, setSubTypes: any) => {
  const subtypes = await getAllSubTypes(`Token ${token.slice(1, -1)}`);
  setSubTypes(subtypes);
};

const getItems = async (
  token: any,
  url:string,
  setBuildings: any,
  setLoading: any
) => {
  try {
  const res = await getBuildings(`Token ${token.slice(1, -1)}`,url);
  if (res.status === "success") {    
    setBuildings(res.data);
  } else {
    console.warn(res);
    setBuildings([]);
  }
  }
  catch (error){
    console.log(error)
  }
  finally {
    setLoading(false)
  }
};

const GoogleCustomersMapV2: React.FC<Props> = () => {
  //set react-geocode settings
  setKey(REACT_APP_GOOGLE_MAPS_KEY || "");
  setLanguage("en");
  setRegion("us");
  // end react-geocode settings
  const [loading, setLoading] = useState(true)
  const url = useMemo(() => new URL(localStorage.getItem("mapUrl") || `${API_URL}/map_buildings`), []);
  const [buildings, setBuildings] = useState([]);
  const [filters,setFilters] = useState<any>({
    'solution':'all',
    'building_type':'all',
    'building_sub_type':'all'
  })
  const [primaryfunctions, setPrimaryfunctions] = useState([]);
  const [subTypes, setSubTypes] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      const token = localStorage.getItem("token");
      getItems(token,url.toString(), setBuildings, setLoading);
      getPrimaryFunctions(token, setPrimaryfunctions);
      getSubTypes(token, setSubTypes);
      const storedFilters = localStorage.getItem('mapFilters')
      if(storedFilters){
        setFilters(JSON.parse(storedFilters))
      }

    }, 3000);
  }, [url]);

  const handleFilter = async (param:string,val:string) => {
    setBuildings([])
    setLoading(true)
  
    filters[param]=val;
    if(param==='building_type' && ! hasSubType()){
        filters['building_sub_type'] = 'all'
        url.searchParams.delete('building_sub_type')
    }
    setFilters(filters)
    if (val=== "all"){
      url.searchParams.delete(param)
    }else{
      url.searchParams.set(param,val)
    }

    setTimeout(()=>{
    const token = localStorage.getItem("token");
    getItems(token,url.toString(), setBuildings, setLoading);
    },1000)
  }
  
  const hasSubType = ()=>{
     return subTypes.some((type: any) => type.property_type === filters['building_type'])
  }

  const storeParams = () => {
    localStorage.setItem('mapFilters', JSON.stringify(filters));
    localStorage.setItem('mapUrl', url.toString());
    const popup = document.getElementById("saved");
    popup?.classList.toggle("show");
    setTimeout(() => {
      const popup = document.getElementById("saved");
      popup?.classList.toggle("show");
    }, 3000);
  }

  return (
    <APIProvider
      apiKey={REACT_APP_GOOGLE_MAPS_KEY || ""}
      key="map"
      version="beta"
    >
      <IonRow className="ion-justify-content-between grey-border-2pt bg-white" style={{width:'100%', margin:'0', borderRadius:'1rem 1rem 0 0', padding:'0.3rem'}}>
        <div className="text-map-main font-18pt font-med font-helvetica" style={{display:'flex',height:'100%', alignItems:'center'}}>Customers Map</div>
        <div className='google-map-filters' style={{display:'flex'}}>
          <IonSelect className="grey-border-2pt" interface="popover" 
            labelPlacement="stacked" 
            label='Building Type' 
            value={filters['building_type']}
            interfaceOptions={{size:'auto'}}
            onIonChange={(e)=>{setLoading(true);handleFilter('building_type',e.detail.value)}}>
            <IonSelectOption value='all'>All</IonSelectOption>
            {primaryfunctions.map((type:any)=>{              
              return <IonSelectOption key={`type-${type.id}`} value={type.id}>{type.name}</IonSelectOption>
            })}
          </IonSelect>
          <IonSelect className="grey-border-2pt" hidden={!hasSubType()} interface="popover" interfaceOptions={{size:'auto'}} labelPlacement="stacked" label='Building Sub-Type' value={filters['building_sub_type']} onIonChange={(e)=>handleFilter('building_sub_type',e.detail.value)}>
            <IonSelectOption value='all'>All</IonSelectOption>
            {
              subTypes.map((type:any)=>{                
                if(filters['building_type']===type.property_type){
                  return <IonSelectOption key={type.uuid} value={type.uuid}>{type.sub_type_name}</IonSelectOption>
                }
                return null
              })
            }
          </IonSelect>
          <IonSelect className="grey-border-2pt" interface='popover' interfaceOptions={{size:'auto'}} labelPlacement="stacked" label='Solution Status' value={filters['solution']} onIonChange={(e)=>handleFilter('solution',e.detail.value)}>
            <IonSelectOption value='all'>All</IonSelectOption>
            <IonSelectOption value='foa'>FOA</IonSelectOption>
            <IonSelectOption value='kpi'>KPI</IonSelectOption>
            <IonSelectOption value='foakpi'>FOA + KPI</IonSelectOption>

          </IonSelect>          
        </div>
      </IonRow>
      <Map
        id="customersMapV2"
        style={{ width: "100% !important", height: "70vh important" }}        
        defaultCenter={{ lat: 42.25419, lng: -89.034959 }}
        defaultZoom={9}
        gestureHandling={"greedy"}
        disableDefaultUI={true}
        mapId={GOOGLE_MAPS_MAP_ID}
      >
      
      <GoogleMapClusters loading={loading} buildings={buildings}/>

        

        
      </Map>
      <div className="popup bg-white" style={{width:'100%',display:'flex',margin:'0',padding:'0.2rem',borderRadius:'0 0 1rem 1rem','justifyContent':'flex-end'}}>        
        <IonButton id='save-btn' fill='clear' slot="icon-only" size="small" color='light' onClick={()=>storeParams()}>
          <IonIcon style={{color:'#2270F1'}} title='Save Filters' src={saveSharp}></IonIcon>
        </IonButton>
        <Popup trigger="save-btn">
          <span>SAVED</span>
        </Popup>
      </div>
    </APIProvider>
  );
};

export default GoogleCustomersMapV2;
